import React, { useEffect } from "react";
import { addDoc, collection, GeoPoint } from "firebase/firestore";
import { useFieldArray, useForm } from "react-hook-form";
import { useFirestore } from "reactfire";

import { I18nKeyType } from "@/i18n";
import { useTranslator } from "@/i18n/useTranslator";

import { AddressField } from "../AddressField";
import { DsButton } from "../ui/DsButton";
import { DsDivider } from "../ui/DsDivider";
import { DsInput } from "../ui/DsInput";
import { DsText } from "../ui/DsText";

// Base type for contacts
type ContactType = {
  name: string;
  email: string;
  phone: string;
};

// Our specific form type that includes all required fields
type CustomerFormDataType = {
  name: string;
  email: string;
  phone: string;
  address: string;
  geopoint: GeoPoint;
  contacts: ContactType[];
};

type NewCustomerFormPropsType = {
  onSuccess: () => void;
};

export const NewCustomerForm = ({ onSuccess }: NewCustomerFormPropsType) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    reset,
  } = useForm<CustomerFormDataType>();

  const { t } = useTranslator();
  const firestore = useFirestore();
  const customersRef = collection(firestore, "customers");

  const onSubmit = async (data: CustomerFormDataType) => {
    const { address, contacts, email, geopoint, name, phone } = data;
    const customerDoc = {
      address,
      contacts,
      email: email === "" ? `${name}@spikesos.com` : email,
      geopoint,
      name,
      phone,
    };

    await addDoc(customersRef, customerDoc);
    reset();
    onSuccess();
  };

  const {
    fields: contacts,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "contacts",
  });

  // make sure at least one contact is added
  useEffect(() => {
    if (contacts.length === 0) {
      append({ name: "", email: "", phone: "" });
    }
  }, [contacts, append]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(onSubmit)(e);
      }}
      className="mt-2"
      autoComplete="off">
      <div className="space-y-4">
        <DsText variant="button">{t("new_customer_form.name")}</DsText>
        <DsInput
          defaultValue=""
          error={errors.name && t("new_customer_form.name_required")}
          {...register("name", { required: true })}
        />
      </div>
      <DsDivider className="my-4" />

      <div className="space-y-4">
        <DsText variant="button">{t("new_customer_form.email")}</DsText>
        <DsInput
          placeholder={"new_customer_form.email_placeholder" as I18nKeyType}
          {...register("email", { pattern: /^(.+)@(.+)(\.)(.+)$/i })}
        />
      </div>
      <DsDivider className="my-4" />

      <div className="space-y-4">
        <DsText variant="button">{t("new_customer_form.phone")}</DsText>
        <DsInput {...register("phone")} />
      </div>
      <DsDivider className="my-4" />

      <div className="space-y-4">
        <DsText variant="button">{t("new_customer_form.address")}</DsText>
        <AddressField
          watch={watch}
          setValue={setValue}
          register={register}
          errors={{
            address: errors.address && {
              type: String(errors.address.type ?? "required"),
              message: String(
                errors.address.message ??
                  t("new_customer_form.address_required"),
              ),
            },
          }}
          errorLabel={t("new_customer_form.address_required")}
          fieldName="address"
        />
      </div>
      <DsDivider className="my-4" />

      <div className="space-y-4">
        <DsText variant="drawerTitle">{t("new_customer_form.contacts")}</DsText>
        <div className="flex flex-col divide-y divide-gray-300">
          {contacts.map((field, index) => (
            <div key={field.id} className="py-4">
              <div className="flex gap-4">
                <div className="bg-[#ffe502] rounded-full w-8 h-8 text-gray-500 flex justify-center items-center">
                  <div>{index + 1}</div>
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <DsText variant="button">
                      {t("new_customer_form.contact_name")}
                    </DsText>
                    <DsInput {...register(`contacts.${index}.name`)} />
                  </div>

                  <div>
                    <DsText variant="button">
                      {t("new_customer_form.contact_email")}
                    </DsText>
                    <DsInput {...register(`contacts.${index}.email`)} />
                  </div>

                  <div>
                    <DsText variant="button">
                      {t("new_customer_form.contact_phone")}
                    </DsText>
                    <DsInput {...register(`contacts.${index}.phone`)} />
                  </div>

                  {contacts.length > 1 && (
                    <DsButton
                      variant="danger"
                      onClick={() => remove(index)}
                      className="mt-2">
                      {t("new_customer_form.remove_contact")}
                    </DsButton>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <DsDivider />
        <DsButton
          type="button"
          variant="secondary"
          onClick={() => append({ name: "", email: "", phone: "" })}
          className="mt-2">
          {t("new_customer_form.add_contact")}
        </DsButton>
      </div>

      <div className="flex mt-4 mb-32">
        <DsButton type="submit">{t("new_customer_form.add_customer")}</DsButton>
      </div>
    </form>
  );
};
